// ============================================================
// TERROR ENGINE — KEYBOARD NAV
// Universal keyboard interaction for modals and menus.
//
// useModalKeys(onClose, opts?)
//   — Escape closes any modal/screen
//   — Enter also triggers close if no options
//   — Optional: Enter triggers a primary action instead
//
// useKeyboardNav(items, onSelect, opts?)
//   — Arrow keys move highlight through a list
//   — Enter activates highlighted item
//   — Returns: { activeIdx, setActiveIdx, itemProps(idx) }
//     itemProps(idx) returns style props to spread on each item
//
// Usage:
//   // Simple close on Escape:
//   useModalKeys(onClose);
//
//   // With highlighted options:
//   const nav = useKeyboardNav(options, (opt) => handlePick(opt));
//   {options.map((opt, i) => (
//     <button {...nav.itemProps(i)} onClick={() => handlePick(opt)}>
//       {opt.label}
//     </button>
//   ))}
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { SD } from "./theme";

// ─────────────────────────────────────────────────────────────
// THE MODAL STACK (2026-08-18) — one keypress, one layer.
//
// Every ENABLED useModalKeys registers on this stack at mount and
// leaves on unmount/disable. Only the TOP of the stack consumes
// Escape/Enter. Before this, every mounted hook carried its own
// window listener and ALL of them fired on the same press — one
// Escape in the Forge closed the card lightbox AND exited the
// whole Forge (observed live 2026-08-18). A modal opened over a
// screen always mounts after it, so layered-later sits on top and
// wins; when it unmounts the layer beneath is top again. Screens
// with a single hook behave exactly as before. Hooks that pass
// enabled:false never occupy the stack, so a gated hook can't
// block the layer beneath it.
// ─────────────────────────────────────────────────────────────
const MODAL_STACK = [];

// ─────────────────────────────────────────────────────────────
// useModalKeys — Escape closes, optional Enter primary action
// ─────────────────────────────────────────────────────────────
export function useModalKeys(onClose, {
  onEnter   = null,   // if set, Enter triggers this instead of onClose
  enabled   = true,
  delay     = 150,    // ms before keys are active (prevent accidental trigger)
} = {}) {
  const ready = useRef(false);
  // Stable identity for this hook INSTANCE — the stack tracks tokens,
  // never callbacks, so re-renders with fresh closures don't reorder it.
  const token = useRef(null);
  if (token.current === null) token.current = {};

  useEffect(() => {
    if (!enabled) return;
    const t = setTimeout(() => { ready.current = true; }, delay);
    return () => { clearTimeout(t); ready.current = false; };
  }, [enabled, delay]);

  // Stack membership follows `enabled`, not just mount.
  useEffect(() => {
    if (!enabled) return;
    MODAL_STACK.push(token.current);
    return () => {
      const i = MODAL_STACK.indexOf(token.current);
      if (i !== -1) MODAL_STACK.splice(i, 1);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const handler = (e) => {
      if (!ready.current) return;
      // Not the top layer → not my keypress. The layer above eats it.
      if (MODAL_STACK[MODAL_STACK.length - 1] !== token.current) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (onEnter) onEnter();
        else onClose?.();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onEnter, enabled]);
}

// ─────────────────────────────────────────────────────────────
// useKeyboardNav — arrow key navigation + Enter selection
//
// items: any[]           — the list being navigated
// onSelect: (item, idx)  — called when Enter pressed on highlighted item
// opts.loop: boolean     — wrap around at ends (default true)
// opts.enabled: boolean  — disable during animations etc (default true)
// opts.startIdx: number  — initial highlight index (default 0)
//
// Returns:
//   activeIdx            — currently highlighted index
//   setActiveIdx         — manually set highlight
//   itemProps(idx)       — spread on each item for highlight + click binding
// ─────────────────────────────────────────────────────────────
export function useKeyboardNav(items, onSelect, {
  loop     = true,
  enabled  = true,
  startIdx = 0,
} = {}) {
  const [activeIdx, setActiveIdx] = useState(startIdx);

  // Clamp activeIdx if items change
  useEffect(() => {
    if (items.length === 0) return;
    setActiveIdx(i => Math.min(i, items.length - 1));
  }, [items.length]);

  useEffect(() => {
    if (!enabled || items.length === 0) return;
    const handler = (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        setActiveIdx(i => {
          const next = i + 1;
          return loop ? next % items.length : Math.min(next, items.length - 1);
        });
      }
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        setActiveIdx(i => {
          const prev = i - 1;
          return loop ? (prev < 0 ? items.length - 1 : prev) : Math.max(prev, 0);
        });
      }
      if (e.key === "Enter") {
        e.preventDefault();
        setActiveIdx(i => {
          onSelect?.(items[i], i);
          return i;
        });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [items, onSelect, loop, enabled]);

  // itemProps — spread onto each list item
  const itemProps = useCallback((idx) => ({
    "data-kb-active": activeIdx === idx,
    onMouseEnter: () => setActiveIdx(idx),
    style: activeIdx === idx ? HIGHLIGHT_STYLE : {},
  }), [activeIdx]);

  return { activeIdx, setActiveIdx, itemProps };
}

// ─────────────────────────────────────────────────────────────
// HIGHLIGHT_STYLE — applied to the active item
// Zones/modals can override via the data-kb-active attribute
// ─────────────────────────────────────────────────────────────
export const HIGHLIGHT_STYLE = {
  outline: `2px solid ${SD.purpleLight}`,
  outlineOffset: "2px",
  boxShadow: `0 0 12px ${SD.purpleLight}66`,
};

// ─────────────────────────────────────────────────────────────
// KeyboardHint — small "↑↓ navigate · Enter select · Esc close"
// Show in any modal that has keyboard nav active
// ─────────────────────────────────────────────────────────────
export function KeyboardHint({ hasNav = true, style = {} }) {
  return (
    <div style={{
      fontSize: 9,
      color: SD.starDim,
      letterSpacing: 1.5,
      textAlign: "center",
      fontFamily: SD.fontDisplay,
      marginTop: 8,
      opacity: 0.7,
      ...style,
    }}>
      {hasNav ? "↑↓ navigate · Enter select · Esc close" : "Esc to close"}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// clickable() — THE HONEST WAY TO MAKE A <div> A BUTTON
// ═════════════════════════════════════════════════════════════
// The a11y audit (`node a11y-audit.mjs`) counts 109 <div onClick>
// sites in Terror. They are invisible to the keyboard and silent to
// a screen reader, and the obvious fix is the wrong one:
//
//     <div role="button" tabIndex={0} onClick={...}>     ← DON'T
//
// That announces "button" and then does not answer Enter or Space.
// It is a lie told to the one user who has no way to check it — a
// sighted mouse user never finds out, so it ships and stays. A
// silent div is at least honest about being scenery.
//
// This spreads the whole contract at once, so the role and the key
// handler can never arrive separately:
//
//     <div {...clickable(() => open(card), `Inspect ${card.name}`)}>
//
// Enter fires on keyDOWN and Space on keyUP, which is what a native
// <button> does and what muscle memory expects — Space held down on
// a real button repeats nothing, and preventDefault on the keydown
// is what stops the page scrolling under you while you hold it.
//
// ── WHEN NOT TO USE THIS ─────────────────────────────────────
// A full-screen scrim whose onClick means "dismiss when you click
// away" is NOT a button. Escape already closes those (see the modal
// stack above), and putting a focusable backdrop in the tab order
// makes every modal cost one extra Tab to get past for no gain.
// Leave them. The audit's clickable-div count will never
// legitimately reach zero and it is not supposed to.
export function clickable(onClick, label, opts = {}) {
  const { disabled = false, role = "button" } = opts;
  if (disabled) return { "aria-disabled": true };
  return {
    role,
    tabIndex: 0,
    "aria-label": label || undefined,
    onClick,
    onKeyDown: (e) => {
      if (e.key === "Enter") { e.preventDefault(); onClick?.(e); }
      // Space must not scroll the page while it is held. The ACT
      // happens on keyup; this only stops the browser's default.
      else if (e.key === " " || e.key === "Spacebar") e.preventDefault();
    },
    onKeyUp: (e) => {
      if (e.key === " " || e.key === "Spacebar") { e.preventDefault(); onClick?.(e); }
    },
  };
}
